import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ISchedule,
  ISlot,
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED,
  PRESENCE_TYPE_IN_PERSON,
  PRESENCE_TYPE_REMOTE,
  PresenceStatus,
  PresenceType
} from '@gettactic/api';
import { api } from '@/lib/api/api';
import { ButtonGroup } from '@gettactic/components/src/components/ButtonGroup/ButtonGroup';
import { ButtonInGroup } from '@gettactic/components/src/components/ButtonGroup/ButtonInGroup';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue
} from '@gettactic/components/src/primitives/Select';
import { cn } from '@gettactic/helpers';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { CampusIcon } from '@cmp-src/icons/TacticIcons/CampusIcon';
import { CameraIcon } from '@cmp-src/icons/TacticIcons/CameraIcon';
import { CrossIcon } from '@cmp-src/icons/TacticIcons/CrossIcon';
import { MinusIcon } from '@cmp-src/icons/TacticIcons/MinusIcon';
import { Button } from '@v2/components/ui/button';
import { JoinButton } from '@cmp-src/components/JoinButton/JoinButton';

export type Props = {
  schedule: ISchedule;
  userSlot: ISlot;
  remoteEnabled?: boolean;
};

type PresenceValue = PresenceType | PresenceStatus;

function getValue(
  type: PresenceType | null,
  status: PresenceStatus
): PresenceValue {
  if (status === PRESENCE_STATUS_ACCEPTED) {
    return type ?? PRESENCE_STATUS_ACCEPTED;
  }
  return status;
}

const BUTTON_IN_GROUP_CLASS_NAME = 'min-w-[89px] border-y-0 font-normal';
const ICON_SIZE_CLASS = 'h-4 w-4';

export const RoomActions = ({
  schedule,
  userSlot,
  remoteEnabled = false
}: Props) => {
  const [presenceStatus, setPresenceStatus] = useState<PresenceStatus>(
    userSlot.presence_status
  );
  const [presenceType, setPresenceType] = useState<PresenceType | null>(
    userSlot.presence_type
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const disabled = !userSlot?.capabilities?.can_respond_presence;
  const queryClient = useQueryClient();

  useEffect(() => {
    setPresenceStatus(userSlot.presence_status);
    setPresenceType(userSlot.presence_type);
  }, [userSlot.presence_status, userSlot.presence_type]);

  const currentValue = getValue(presenceType, presenceStatus);

  const onApprovalPresence = useCallback(async (
  status: PresenceStatus,
  presenceType: PresenceType | null
): Promise<void> => {
  const prevPresenceStatus = presenceStatus;
  const prevPresenceType = presenceType;

  setIsEditing(true);
  try {
    await api.client.schedules.presence(
      schedule.id,
      userSlot.id,
      status,
      presenceType ?? undefined
    );
    await queryClient.invalidateQueries(['schedules']);
    await queryClient.invalidateQueries(['myAgenda'], { exact: false });
    await queryClient.invalidateQueries(['schedule', schedule.id]);
    await queryClient.invalidateQueries(['tableOverview']);
  } catch (error) {
    toast.error('Failed to update presence status');
    setPresenceStatus(prevPresenceStatus);
    setPresenceType(prevPresenceType);
  } finally {
    setIsEditing(false);
  }
}, [schedule.id, userSlot.id, queryClient]);

  const handleYes = useCallback(() => {
    setPresenceStatus(PRESENCE_STATUS_ACCEPTED);
    setPresenceType(PRESENCE_TYPE_IN_PERSON);
    onApprovalPresence(PRESENCE_STATUS_ACCEPTED, PRESENCE_TYPE_IN_PERSON);
  }, [onApprovalPresence]);

  const handleNo = useCallback(() => {
    setPresenceStatus(PRESENCE_STATUS_REJECTED);
    setPresenceType(null);
    onApprovalPresence(PRESENCE_STATUS_REJECTED, null);
  }, [onApprovalPresence]);

  const handleMaybe = useCallback(() => {
    setPresenceStatus(PRESENCE_STATUS_PENDING);
    setPresenceType(null);
    onApprovalPresence(PRESENCE_STATUS_PENDING, null);
  }, [onApprovalPresence]);

  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === PRESENCE_TYPE_IN_PERSON || value === PRESENCE_TYPE_REMOTE) {
        setPresenceStatus(PRESENCE_STATUS_ACCEPTED);
        setPresenceType(value as PresenceType);
        onApprovalPresence(PRESENCE_STATUS_ACCEPTED, value as PresenceType);
      } else {
        setPresenceStatus(value as PresenceStatus);
        setPresenceType(null);
        onApprovalPresence(value as PresenceStatus, null);
      }
    },
    [onApprovalPresence]
  );

  const hasResponse =
    presenceStatus !== PRESENCE_STATUS_PENDING || presenceType !== null;

  const selectOptions = useMemo(
    () =>
      [
        {
          value: PRESENCE_TYPE_REMOTE,
          label: 'Remote',
          disabled: !remoteEnabled,
          icon: <CameraIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_TYPE_IN_PERSON,
          label: 'In room',
          icon: <CampusIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_STATUS_PENDING,
          label: 'Maybe',
          icon: <MinusIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_STATUS_REJECTED,
          label: 'Decline',
          icon: <CrossIcon className={ICON_SIZE_CLASS} />
        }
      ].filter((option) => !option.disabled),
    [remoteEnabled]
  );

  if (!hasResponse) {
    return (
      <ControlWrapper>
        <ButtonGroup className="rounded-xl border-1" disabled={disabled}>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleYes}
            disabled={disabled}
          >
            Yes
          </ButtonInGroup>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleNo}
            disabled={disabled}
          >
            No
          </ButtonInGroup>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleMaybe}
            disabled={disabled}
          >
            Maybe
          </ButtonInGroup>
        </ButtonGroup>
      </ControlWrapper>
    );
  }

  return (
    <div className="flex flex-row gap-2">
      
    <ControlWrapper>
      <Select
        value={currentValue}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger className={cn("flex items-center justify-stretch gap-1 rounded-md ring-1 ring-neutral-50",
          presenceType ? 'w-[127px]' : ''
        )}>
          {/* Uncomment if we want to show the icon */}
          {/* {selectOptions.find((option) => option.value === currentValue)?.icon} */}
          <div className="flex flex-1 items-center justify-center">
            <SelectValue className="flex items-center justify-center text-neutral-100" 
              placeholder="Choose presence type">
              {presenceType === PRESENCE_TYPE_IN_PERSON ||
              (presenceStatus === PRESENCE_STATUS_ACCEPTED && !presenceType)
                ? 'Yes'
                : selectOptions.find((option) => option.value === currentValue)
                    ?.label}
            </SelectValue>
          </div>
          <div className="text-neutral-100">|</div>
        </SelectTrigger>
        <SelectContent>
          {selectOptions.map((option) => (
            <SelectPrimitive.Item
              key={option.value}
              value={option.value}
              className={cn(
                'relative flex w-full cursor-default select-none items-center rounded-md bg-background px-1 py-1.5 text-sm outline-none',
                'hover:bg-neutral-50 focus:bg-neutral-50 focus:text-accent-foreground',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
              )}
            >
              <span
                className={cn(
                  'mr-[10px] flex items-center justify-center',
                  // iconSizeClass is here to keep the icon behind the checkmark,
                  // not near it on the same row
                  ICON_SIZE_CLASS
                )}
              >
                <SelectPrimitive.ItemIndicator>
                  <Check className={ICON_SIZE_CLASS} />
                </SelectPrimitive.ItemIndicator>
                {option.icon}
              </span>
              <SelectPrimitive.ItemText>
                {option.label}
              </SelectPrimitive.ItemText>
            </SelectPrimitive.Item>
          ))}
        </SelectContent>
      </Select>
    </ControlWrapper>
    </div>
  );
};

const ControlWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col gap-2">{children}</div>;
};
